<template>
  <div class="h-full w-full max-w-full rounded-lg border border-cockpit-line overflow-hidden shadow-cockpit">
    <FlexCol class="w-full h-full max-w-full relative table-outer bg-cockpit-surface text-cockpit-text">
      <div class="grow overflow-auto">
        <table class="w-full grow" :class="{'table-fixed': tableFixed}">
          <thead class="header top-0 h-10 uppercase">
            <tr v-if="headers" class="bg-cockpit-elevated border-b border-cockpit-line text-cockpit-green">
              <th
                v-for="(header, i) in headers"
                :key="i"
                class="text-nowrap select-none"
                :class="[cellSpacing, i === headers.length - 1 && stickyActions ? 'sticky right-0 bg-cockpit-elevated z-[3]' : '']"
                :style="header.width ? {width: header.width} : undefined">
                <Flex gap-3 items-center :class="[header.headerClass]">
                  <slot :name="`td-${header.field}`" :item="header">
                    {{ header.title }}

                    <div v-if="!header.disableOrder">
                      <i class="fa-solid fa-sort-down cursor-pointer ml-2" @click="setOrder(header, 2, header.table)" v-if="getStatus(header) == 1" />
                      <i class="fa-solid fa-sort-up cursor-pointer ml-2" @click="setOrder(header, 0, header.table)" v-else-if="getStatus(header) == 2" />
                      <i class="fa-solid fa-sort cursor-pointer ml-2" @click="setOrder(header, 1, header.table)" v-else="getStatus(header)" />
                    </div>
                  </slot>
                </Flex>
              </th>
              <th v-if="expanded" :class="[cellSpacing]"></th>
            </tr>
          </thead>

          <tbody>
            <template v-for="(item, index) in processedItems" :key="index">
              <tr
                @click="sendLeftClick(item)"
                @click.right.prevent="sendRightClick(item)"
                @dblclick="sendDoubleClick(item)"
                class="cursor-pointer group"
                :class="[
                  computedClass(item),
                  { 'border-b border-cockpit-line': !expanded || !item.expanded },
                  { '!bg-cockpit-green/10 !text-cockpit-green': compareItem(item) },
                  { 'hover:bg-cockpit-green/[0.06]': !compareItem(item) },
                  { 'text-cockpit-critical': item.errorLine },
                  { 'divide-x': cellBorder },
                ]">
                <td
                  v-for="(header, hIndex) in headers"
                  :key="header"
                  :class="[header.itemClass, 'text-nowrap', cellSpacing, hIndex === headers.length - 1 && stickyActions ? 'sticky right-0' : '']"
                  :style="header.width ? {width: header.width} : undefined">
                  <div v-if="item" class="h-full" :class="[header.title === 'Ações' ? 'flex justify-center' : '']">
                    <slot :name="header.field" :item="item" :rowIndex="index">
                      {{ getItemValue(item, header) }}
                    </slot>
                  </div>
                </td>

                <td v-if="expanded && mapNotExpandedItems(item)" class="w-4">
                  <IconSolid :icon="item.expanded ? 'fa-chevron-down' : 'fa-chevron-up'" @click="item.expanded = !item.expanded" />
                </td>
              </tr>

              <TransitionX>
                <tr v-if="expanded || item.expanded" class="border-b border-cockpit-line bg-cockpit-elevated">
                  <td :colspan="headers?.length + 1">
                    <slot name="expanded" :item="item"></slot>
                  </td>
                </tr>
              </TransitionX>
            </template>
          </tbody>

          <tfoot>
            <slot name="footer" />
          </tfoot>
        </table>
      </div>
      <div class="relative w-full h-[2px] overflow-hidden bg-cockpit-line" v-if="loading">
        <div class="absolute inset-y-0 left-0 w-1/3 bg-cockpit-green animate-[loading_1s_linear_infinite]"></div>
      </div>
      <div class="table-footer bg-cockpit-surface border-t border-cockpit-line p-2">
        <p class="text-center italic py-5" v-if="items && items?.length < 1">Nenhum item a ser exibido.</p>
        <div v-if="!disablePagination">
          <Pagination
            v-if="items?.length"
            v-model="localControl.page"
            v-model:limit="localControl.limit"
            :disablePagination="disablePagination"
            :totalItems="localControl.total"
            :totalPages="localControl.pages">
            <template #left>
              <Flex v-if="!disableOrder" gap-3 center class="border-l-2 border-cockpit-line pl-2 text-cockpit-muted">
                Ordenado por:
                <div v-for="(order, i) in localControl.order" :key="i" class="border border-cockpit-line bg-cockpit-canvas py-1 px-3 text-xs rounded text-cockpit-muted">
                  {{ order.name }}
                  <i class="fa-solid fa-caret-down cursor-pointer ml-2" v-if="order.direction == 'DESC'" />
                  <i class="fa-solid fa-caret-up cursor-pointer ml-2" v-if="order.direction == 'ASC'" />
                  <i class="fa-solid fa-x cursor-pointer text-[0.8em] ml-2" @click="removeOrder(order.column)" />
                </div>
              </Flex>
            </template>
          </Pagination>
        </div>
      </div>
    </FlexCol>
  </div>

  <div class="text-red-400 text-xs mt-3 flex justify-end" v-if="error">
    <b>{{ error }}</b>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, onBeforeUnmount } from 'vue'

const emit = defineEmits(['update:modelValue', 'update:loading', 'selectedItem', 'rightClick', 'doubleClick'])

const exampleHeader = {
  title: 'Nome',
  field: 'name',
  table: 'Tabela Referência no banco',
  lineClass: 'Classes para cada TR',
  itemClass: 'Classe para cada TD',
  headerClass: 'Classe para cada TH',
}

const props = defineProps({
  modelValue: Object,
  headers: {
    type: Array,
    default: [],
  },
  items: {
    type: Array,
    default: [],
  },
  dense: Boolean,
  ultraDense: Boolean,
  expanded: Boolean,
  loading: Boolean,
  disableOrder: Boolean,
  disablePagination: Boolean,
  offline: Boolean,
  stickyActions: {
    type: Boolean,
    default: false,
  },
  tableFixed: Boolean,
})
// Local Values
const localControl = ref(props.modelValue)
const selectedItem = ref(null)
//  Computed
const cellSpacing = computed(() => {
  if (props.ultraDense) {
    return 'py-[2px] px-[5px] text-[0.8em]'
  }
  if (props.dense) {
    return 'px-2 py-1 text-sm'
  }
  return 'px-5 py-2'
})

const processedItems = computed(() => {
  let items = props.items || []

  // Se estiver em modo offline, aplica ordenação e paginação localmente
  if (props.offline && items.length > 0) {
    // Ordenação local
    if (localControl.value.order && localControl.value.order.length > 0) {
      items = [...items] // Clone para não mutar o array original

      items.sort((a, b) => {
        for (const orderRule of localControl.value.order) {
          const column = orderRule.column
          let aVal = a[column]
          let bVal = b[column]

          // Tratamento de valores nulos/undefined
          if (aVal == null && bVal == null) continue
          if (aVal == null) return 1
          if (bVal == null) return -1

          // Comparação de strings (case insensitive)
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            aVal = aVal.toLowerCase()
            bVal = bVal.toLowerCase()
          }

          let comparison = 0
          if (aVal < bVal) comparison = -1
          else if (aVal > bVal) comparison = 1

          if (comparison !== 0) {
            return orderRule.direction === 'DESC' ? -comparison : comparison
          }
        }
        return 0
      })
    }

    // Paginação local
    if (localControl.value.limit && localControl.value.page) {
      const start = (localControl.value.page - 1) * localControl.value.limit
      const end = start + localControl.value.limit

      // Atualiza o total e páginas para refletir o array completo
      localControl.value.total = items.length
      localControl.value.pages = Math.ceil(items.length / localControl.value.limit)

      items = items.slice(start, end)
    }
  }

  // Aplica expansão se necessário
  if (props.startsExpanded) {
    return items.map((item) => ({
      ...item,
      expanded: true,
    }))
  }

  return items
})

const computedClass = (item) => {
  return item.lineClass
}
// Senders
const setOrder = (header, value, tableName) => {
  const columnName = header.orderField ?? header.field

  //ex basico: { title: 'Código', field: 'id' },
  //ex com join: { title: 'Depósito', field: 'warehouseName', table: 'warehouse', orderField: 'name' }

  const newOrder = {
    name: header.title,
    table: tableName,
    column: columnName,
    direction: value == 1 ? 'DESC' : 'ASC',
  }

  if (header.raw) {
    newOrder.raw = header.raw
  }

  // Lógica unificada para online e offline (permite múltiplas ordenações)
  const index = localControl.value.order.findIndex((el) => {
    if (tableName) {
      return el.column === columnName && el.table === tableName
    }
    return el.column === columnName
  })

  if (index < 0 && value > 0) {
    localControl.value.order.push(newOrder)
  } else if (index >= 0 && value > 0) {
    localControl.value.order[index] = newOrder
  } else if (index >= 0 && value == 0) {
    localControl.value.order.splice(index, 1)
  }
}

const getStatus = (header) => {
  const columnName = header.orderField ?? header.field
  const hasTable = !!header.table

  const index = localControl.value.order.findIndex((el) => {
    if (hasTable) {
      return el.column === columnName && el.table === header.table
    }
    return el.column === columnName
  })

  if (index === -1) return 0

  return localControl.value.order[index].direction === 'DESC' ? 1 : 2
}

const removeOrder = (column) => {
  const index = localControl.value.order.findIndex((el) => el.column == column)
  localControl.value.order.splice(index, 1)
}

const sendRightClick = (item) => {
  emit('rightClick', item)
}

const sendDoubleClick = (item) => {
  emit('doubleClick', item)
}

const sendLeftClick = (item) => {
  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const compareItem = (item) => {
  return JSON.stringify(item) == selectedItem.value
}

const getItemValue = (item, header) => {
  const rawValue = item[header.field]
  if (header.format && typeof header.format == 'function') {
    const formattedValue = header.format(rawValue, item)
    return formattedValue
  }
  return rawValue
}

const mapNotExpandedItems = (item) => {
  if (!props.notExpandFieldsWith || props.notExpandFieldsWith?.length === 0) {
    return true
  }

  const conditions = props.notExpandFieldsWith.map((field) => {
    return item[field.field] !== field.value
  })

  return conditions.every(Boolean)
}
/* =========================================================
 * Extra - Navegação por teclado (ArrowUp / ArrowDown)
 * ========================================================= */
const getSelectedIndex = () => {
  if (!selectedItem.value) return -1
  const list = processedItems.value || []
  return list.findIndex((it) => JSON.stringify(it) === selectedItem.value)
}

const selectByIndex = (index) => {
  const list = processedItems.value || []
  if (!list.length) return

  const bounded = Math.max(0, Math.min(index, list.length - 1))
  const item = list[bounded]

  selectedItem.value = JSON.stringify(item)
  emit('selectedItem', item)
}

const moveSelection = (delta) => {
  const list = processedItems.value || []
  if (!list.length) return

  const currentIdx = getSelectedIndex()

  // se ainda não tem seleção, começa pela primeira linha (ou última se subir)
  if (currentIdx === -1) {
    selectByIndex(delta > 0 ? 0 : list.length - 1)
    return
  }

  selectByIndex(currentIdx + delta)
}

const onKeyDown = (e) => {
  // não roubar teclado de input/textarea/editor
  const tag = e.target?.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    moveSelection(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    moveSelection(-1)
  }
}
/* ========================= end adicionado ========================= */
//  Watchers
watch(
  () => localControl.value,
  (newValue) => {
    // emit(['update:modelValue', newValue])
  },
  { deep: true },
)
//  Life Cicle
onMounted(() => {
  window.addEventListener('keydown', onKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown)
})
</script>

<style>
.table-outer {
  overflow-x: auto;
}

thead {
  position: sticky;
  z-index: 3;
  top: 0;
}

.table-header {
  height: 40px;
}

.table-footer {
  position: sticky;
  z-index: 2;
  bottom: 0;
}

@keyframes loading {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}
</style>
